import { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Phone, ShieldAlert, Check, X, RefreshCcw, MessageSquare, ArrowRightLeft } from "lucide-react";
import { useGlobalViewMode } from "../components/ViewModeToggle";

const STATUS_FILTERS = [
  { key: "Pending", label: "Pending", tone: "bg-amber-100 text-amber-800" },
  { key: "Approved", label: "Approved", tone: "bg-emerald-100 text-emerald-800" },
  { key: "Rejected", label: "Rejected", tone: "bg-rose-100 text-rose-800" },
  { key: "All", label: "All", tone: "bg-slate-200 text-slate-800" },
];

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return s; }
}

export default function ContactDeleteRequests({ embedded = false } = {}) {
  const [tab, setTab] = useState("delete"); // delete | transfer
  const [deleteItems, setDeleteItems] = useState([]);
  const [transferItems, setTransferItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // {kind, request, decision}
  const [adminRemark, setAdminRemark] = useState("");
  const [busy, setBusy] = useState(false);

  const items = tab === "transfer" ? transferItems : deleteItems;

  const load = async () => {
    setLoading(true);
    try {
      const [del, trf] = await Promise.all([
        api.get(`/admin/contact-delete-requests?status=${encodeURIComponent(statusFilter)}`),
        api.get(`/admin/contact-transfer-requests?status=${encodeURIComponent(statusFilter)}`),
      ]);
      setDeleteItems(del.data || []);
      setTransferItems(trf.data || []);
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const decide = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const url = confirm.kind === "transfer"
        ? `/admin/contact-transfer-requests/${confirm.request.id}/decide`
        : `/admin/contact-delete-requests/${confirm.request.id}/decide`;
      await api.post(url, { decision: confirm.decision, remark: adminRemark });
      const verb = confirm.decision === "Approve"
        ? (confirm.kind === "transfer" ? "approved — contact transferred" : "approved — contact removed")
        : "rejected";
      toast.success(`Request ${verb}`);
      setConfirm(null);
      setAdminRemark("");
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className={embedded ? "space-y-4" : "space-y-5"} data-testid="contact-delete-requests-page">
      {!embedded && (
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="heading text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
              <ShieldAlert size={22} className="text-rose-600" />
              Contact Approvals
            </h1>
            <p className="text-[12.5px] text-slate-500 mt-1">
              Master-Admin review queue for contact deletion <i>and</i> transfer requests submitted from CRM Reporting and Client Database.
            </p>
          </div>
          <Button variant="outline" onClick={load} data-testid="cdr-refresh">
            <RefreshCcw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      )}
      {embedded && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[12px] text-slate-500">
            Master-Admin review queue for contact deletion &amp; transfer requests.
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="cdr-refresh">
            <RefreshCcw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="delete" data-testid="cdr-tab-delete">
            <ShieldAlert size={13} className="mr-1.5" /> Deletions ({deleteItems.length})
          </TabsTrigger>
          <TabsTrigger value="transfer" data-testid="cdr-tab-transfer">
            <ArrowRightLeft size={13} className="mr-1.5" /> Transfers ({transferItems.length})
          </TabsTrigger>
        </TabsList>

        <div className="flex items-center gap-2 flex-wrap mt-3">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.key}
              onClick={() => setStatusFilter(s.key)}
              className={`h-8 px-3 text-[11px] font-bold rounded-full border transition-all ${
                statusFilter === s.key
                  ? "bg-rose-600 text-white border-rose-600"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
              data-testid={`cdr-filter-${s.key.toLowerCase()}`}
            >{s.label}</button>
          ))}
          <span className="ml-auto text-[11.5px] text-slate-500">{items.length} request(s)</span>
        </div>

        <TabsContent value="delete" className="mt-3">
          <RequestList
            items={deleteItems}
            loading={loading}
            kind="delete"
            statusFilter={statusFilter}
            onAct={(req, decision) => { setConfirm({ kind: "delete", request: req, decision }); setAdminRemark(""); }}
          />
        </TabsContent>
        <TabsContent value="transfer" className="mt-3">
          <RequestList
            items={transferItems}
            loading={loading}
            kind="transfer"
            statusFilter={statusFilter}
            onAct={(req, decision) => { setConfirm({ kind: "transfer", request: req, decision }); setAdminRemark(""); }}
          />
        </TabsContent>
      </Tabs>

      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) { setConfirm(null); setAdminRemark(""); } }}>
        <DialogContent className="max-w-md" data-testid="cdr-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirm?.decision === "Approve"
                ? (confirm?.kind === "transfer" ? "Approve transfer?" : "Approve deletion?")
                : (confirm?.kind === "transfer" ? "Reject transfer?" : "Reject deletion?")}
            </DialogTitle>
          </DialogHeader>
          <div className="text-[12.5px] space-y-2">
            <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
              <b>{confirm?.request.contact_name || "(no name)"}</b> · {confirm?.request.contact_number}
              <div className="text-[11px] text-slate-500 mt-0.5">From <b>{confirm?.request.client_name}</b></div>
              {confirm?.kind === "transfer" && (
                <div className="text-[11px] text-slate-700 mt-1">
                  → U-DISE <b>{confirm?.request.target_udise}</b>
                  {confirm?.request.target_client_name && <span className="text-slate-500"> ({confirm.request.target_client_name})</span>}
                </div>
              )}
            </div>
            <div className="text-[11.5px] text-slate-600">
              Requested by <b>{confirm?.request.requested_by_name}</b> — &quot;{confirm?.request.remark}&quot;
            </div>
            <div>
              <label className="text-[10.5px] uppercase font-bold text-slate-600">Admin note (optional)</label>
              <Input
                value={adminRemark}
                onChange={(e) => setAdminRemark(e.target.value)}
                placeholder="Add a note for the requester (optional)"
                className="mt-1"
                data-testid="cdr-admin-remark"
              />
            </div>
            {confirm?.decision === "Approve" && (
              <div className="text-[11px] text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                {confirm?.kind === "transfer"
                  ? "Approving will move this contact from the source school to the target U-DISE. The target school must already exist in the client database."
                  : "Approving will permanently remove this contact from the client's record. This cannot be undone."}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setConfirm(null); setAdminRemark(""); }}>Cancel</Button>
            <Button
              onClick={decide}
              disabled={busy}
              className={confirm?.decision === "Approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}
              data-testid="cdr-submit"
            >
              {busy
                ? "Submitting…"
                : (confirm?.decision === "Approve"
                    ? (confirm?.kind === "transfer" ? "Approve & Transfer" : "Approve & Remove")
                    : (confirm?.kind === "transfer" ? "Reject Transfer" : "Reject Request"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RequestList({ items, loading, kind, statusFilter, onAct }) {
  const [viewMode] = useGlobalViewMode();
  if (loading) return <div className="rounded-xl border border-slate-200 bg-white px-4 py-10 text-center text-[12.5px] text-slate-500">Loading…</div>;
  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-12 text-center" data-testid={`cdr-empty-${kind}`}>
        <ShieldAlert size={28} className="text-slate-300 mx-auto mb-2" />
        <div className="text-[13px] font-semibold text-slate-700">No {statusFilter.toLowerCase()} {kind === "transfer" ? "transfer" : "deletion"} requests</div>
      </div>
    );
  }
  if (viewMode === "card") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid={`cdr-cards-${kind}`}>
        {items.map((r) => {
          const tone = STATUS_FILTERS.find((s) => s.key === r.status)?.tone || "bg-slate-200 text-slate-700";
          return (
            <div key={r.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`cdr-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-slate-900 truncate">{r.client_name}</div>
                  <div className="text-[10px] mono text-slate-500 truncate">U-Dise · {r.source_udise || r.u_dise || "—"}</div>
                  {kind === "transfer" && (
                    <div className="text-[11px] text-amber-700 mt-1 flex items-center gap-1 truncate">
                      <ArrowRightLeft size={11} /> {r.target_udise || "—"}
                      {r.target_client_name && <span className="text-slate-500">· {r.target_client_name}</span>}
                    </div>
                  )}
                </div>
                <Badge className={`${tone} text-[10px] shrink-0`}>{r.status}</Badge>
              </div>
              <div className="text-[11.5px] mb-2">
                <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><Phone size={9} /> Contact</div>
                <div className="text-slate-900 font-semibold">{r.contact_name || <span className="italic text-slate-400">(no name)</span>}</div>
                <div className="mono text-slate-600 text-[11px]">{r.contact_number}</div>
                {r.contact_role && <div className="text-[10.5px] text-slate-500">{r.contact_role}</div>}
              </div>
              <div className="text-[11.5px] mb-2">
                <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><MessageSquare size={9} /> Reason</div>
                <div className="text-slate-700 italic line-clamp-2">&quot;{r.remark}&quot;</div>
                <div className="text-[10.5px] text-slate-500 mt-1">By <b>{r.requested_by_name}</b> · {fmtDate(r.created_at)}</div>
                {r.decided_by_name && (
                  <div className="text-[10.5px] text-slate-500 mt-1">
                    {r.status} by <b>{r.decided_by_name}</b> · {fmtDate(r.decided_at)}
                    {r.decision_remark && <div className="italic line-clamp-1">&quot;{r.decision_remark}&quot;</div>}
                  </div>
                )}
              </div>
              {r.status === "Pending" && (
                <div className="pt-2 border-t border-slate-100 flex items-center justify-end gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px] text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => onAct(r, "Reject")} data-testid={`cdr-reject-card-${r.id}`}>
                    <X size={10} className="mr-1" /> Reject
                  </Button>
                  <Button size="sm" className="h-7 px-2 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onAct(r, "Approve")} data-testid={`cdr-approve-card-${r.id}`}>
                    <Check size={10} className="mr-1" /> Approve
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="divide-y divide-slate-100">
          {items.map((r) => {
            const tone = STATUS_FILTERS.find((s) => s.key === r.status)?.tone || "bg-slate-200 text-slate-700";
            return (
              <div key={r.id} className="px-4 py-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-start" data-testid={`cdr-row-${r.id}`}>
                <div className="md:col-span-3">
                  <div className="text-[13px] font-semibold text-slate-900">{r.client_name}</div>
                  <div className="text-[10.5px] mono text-slate-500">U-Dise · {r.source_udise || r.u_dise || "—"}</div>
                  {kind === "transfer" && (
                    <div className="text-[11px] text-amber-700 mt-1 flex items-center gap-1">
                      <ArrowRightLeft size={11} /> {r.target_udise || "—"}
                      {r.target_client_name && <span className="text-slate-500">· {r.target_client_name}</span>}
                    </div>
                  )}
                </div>
                <div className="md:col-span-3">
                  <div className="text-[13px] font-semibold text-slate-900 flex items-center gap-1.5">
                    <Phone size={12} className="text-slate-400" />
                    {r.contact_name || <span className="italic text-slate-400">(no name)</span>}
                  </div>
                  <div className="text-[11.5px] mono text-slate-600">{r.contact_number}</div>
                  {r.contact_role && <div className="text-[10.5px] text-slate-500">{r.contact_role}</div>}
                </div>
                <div className="md:col-span-3">
                  <div className="text-[10.5px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1"><MessageSquare size={10} /> Reason</div>
                  <div className="text-[12.5px] text-slate-700 italic mt-0.5">&quot;{r.remark}&quot;</div>
                  <div className="text-[10.5px] text-slate-500 mt-1">
                    By <b>{r.requested_by_name}</b> · {fmtDate(r.created_at)}
                  </div>
                  {r.decided_by_name && (
                    <div className="text-[10.5px] text-slate-500 mt-1">
                      {r.status} by <b>{r.decided_by_name}</b> · {fmtDate(r.decided_at)}
                      {r.decision_remark && <div className="italic">&quot;{r.decision_remark}&quot;</div>}
                    </div>
                  )}
                </div>
                <div className="md:col-span-3 flex items-center justify-end gap-2">
                  <Badge className={`${tone} text-[10px]`}>{r.status}</Badge>
                  {r.status === "Pending" && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50"
                        onClick={() => onAct(r, "Reject")}
                        data-testid={`cdr-reject-${r.id}`}
                      >
                        <X size={12} className="mr-1" /> Reject
                      </Button>
                      <Button
                        size="sm"
                        className="h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => onAct(r, "Approve")}
                        data-testid={`cdr-approve-${r.id}`}
                      >
                        <Check size={12} className="mr-1" /> Approve
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
    </div>
  );
}
